// Instagram インスタントウィン シナリオエディタ Store

import { defineStore } from 'pinia'

import {
  InstagramScenarioContainerNode,
  InstagramScenarioContainerNodeOption,
  InstagramScenarioContainerResponseData
} from '../api/getInstagramScenarioContainer'
import { getInstagramScenarioContainer } from '../api/getInstagramScenarioContainer'
import { putInstagramScenarioContainers } from '../api/putInstagramScenarioContainers'

/**
 * API取得データ用型定義 (any除去)
 */
export interface ApiSelectOptionNode {
  id?: number
  text?: string
}
export interface ApiCardButton {
  id?: number
  name?: string
  url?: string
  tempId?: string
}
export interface ApiMedia {
  media_id?: number
  url?: string
  fileType?: string
  mediaOriginName?: string
}
export interface ApiCard {
  mediaId?: number | null
  url?: string | null
  title?: string
  subtitle?: string
  cardUrl?: string
  buttons?: ApiCardButton[]
  mediaOriginName?: string
}

export type ApiNodeType =
  | 'FIRST_NODE'
  | 'TEXT'
  | 'MEDIA'
  | 'CARD'
  | 'SELECT'
  | 'SELECT_OPTION'
  | 'LOTTERY_NODE'
  | 'LOTTERY_WIN'
  | 'LOTTERY_LOSE'
export interface ApiNode {
  id?: number
  tempId?: string
  parentIds?: number[]
  parentTempIds?: string[]
  type: ApiNodeType | number
  isFinally?: boolean
  text?: string
  media?: ApiMedia
  card?: ApiCard
  selectOption?: ApiSelectOptionNode[]
}
export interface ApiContainer {
  containerId: number
  nodes: ApiNode[]
}

/** 保存リクエスト用 */
export interface RequestCardButton {
  name: string
  url: string
  id?: number
  tempId?: string
}
export interface RequestCard {
  title: string
  subtitle: string
  url: string | null
  mediaId: number | null
  button: RequestCardButton[]
}
export interface RequestNode {
  id?: number
  tempId?: string
  parentIds?: number[]
  parentTempIds?: string[]
  type: number
  isFinally: boolean
  text?: string
  mediaId?: number
  card?: RequestCard
}
export interface RequestContainer {
  id: number | null
  stepOrder: number
  nodes: RequestNode[]
}

/**
 * メッセージノードの共通型
 */
type MessageNodeBase = {
  type: 'text' | 'media' | 'select' | 'card'
}

/**
 * テキストノード
 */
export type TextNode = MessageNodeBase & {
  type: 'text'
  text: string
  id?: number | null // 既存ノードID（更新時）
}

/**
 * メディアノード
 */
export type MediaNode = MessageNodeBase & {
  type: 'media'
  mediaId: number | null
  url: string | null
  fileType?: 'image' | 'video'
  fileName?: string // オリジナルファイル名表示用
  id?: number | null // 既存ノードID
}

/**
 * アンケートノード
 */
export type SelectNode = MessageNodeBase & {
  type: 'select'
  question: string
  options: Array<{ text: string; id?: number | null }>
  id?: number | null // 既存SELECTノードID
}

/**
 * カードノード
 */
export type CardNode = MessageNodeBase & {
  type: 'card'
  mediaId: number | null
  url: string | null
  fileName?: string // オリジナルファイル名表示用
  title: string
  subtitle: string
  defaultUrl: string
  buttons: Array<{ label: string; url: string; id?: number | null }>
  id?: number | null // 既存CARDノードID
}

/**
 * メッセージノードの型（Union型）
 */
export type MessageNode = TextNode | MediaNode | SelectNode | CardNode

/**
 * フォローチェック設定
 */
export type FollowCheck = {
  enabled: boolean
  confirmMessage: string
  notFollowingMessage: string
}

/**
 * 抽選中メッセージ
 */
export type Lottery = {
  text: string
  mediaId: number | null
  url: string | null
  fileType?: 'image' | 'video' // blob URLの場合にファイルタイプを保存
  fileName?: string // オリジナルのファイル名
}

/**
 * 当落メッセージ
 */
export type Result = {
  winMessage: string
  winMediaId: number | null
  winUrl: string | null
  winFileType?: 'image' | 'video' // blob URLの場合にファイルタイプを保存
  winFileName?: string // オリジナルのファイル名
  loseMessage: string
  loseMediaId: number | null
  loseUrl: string | null
  loseFileType?: 'image' | 'video' // blob URLの場合にファイルタイプを保存
  loseFileName?: string // オリジナルのファイル名
}

/**
 * バリデーションエラー
 */
export type ValidationErrors = {
  [step: number]: {
    [field: string]: string | null
  }
}

/**
 * Instagram シナリオエディタストアの状態
 */
export type InstagramScenarioEditorState = {
  prizeId: number | null
  containerIds: {
    step1: number | null
    step2: number | null
    step3: number | null
    step4: number | null
  }
  currentStep: 1 | 2 | 3 | 4
  followCheck: FollowCheck
  messages: MessageNode[]
  lottery: Lottery
  result: Result
  nodeIdMap: {
    followCheck: {
      selectId: number | null
      optionYesId: number | null
      optionNoId: number | null
      notFollowingId: number | null
    }
    messages: {
      firstNodeId: number | null
      nodeIds: Array<number | null>
      selectOptionNodeIds: Array<Array<number | null>>
    }
    lottery: {
      textNodeId: number | null
      mediaNodeId: number | null
    }
    result: {
      lotteryNodeId: number | null
      winNodeId: number | null
      winTextNodeId: number | null
      winMediaNodeId: number | null
      loseNodeId: number | null
      loseTextNodeId: number | null
      loseMediaNodeId: number | null
    }
  }
  errors: ValidationErrors
  savedData: {
    followCheck: FollowCheck
    messages: MessageNode[]
    lottery: Lottery
    result: Result
  } | null

  campaignRunning: boolean
  uploadLoading: boolean
}

// 以降旧ファイル内容そのまま（getter / actions）
export const useInstagramScenarioEditorStore = defineStore('instagramScenarioEditor', {
  state: (): InstagramScenarioEditorState => ({
    prizeId: null,
    containerIds: { step1: null, step2: null, step3: null, step4: null },
    currentStep: 1,
    followCheck: { enabled: true, confirmMessage: '', notFollowingMessage: '' },
    messages: [],
    lottery: { text: '', mediaId: null, url: null, fileType: undefined, fileName: undefined },
    result: {
      winMessage: '',
      winMediaId: null,
      winUrl: null,
      winFileType: undefined,
      winFileName: undefined,
      loseMessage: '',
      loseMediaId: null,
      loseUrl: null,
      loseFileType: undefined,
      loseFileName: undefined
    },
    nodeIdMap: {
      followCheck: { selectId: null, optionYesId: null, optionNoId: null, notFollowingId: null },
      messages: { firstNodeId: null, nodeIds: [], selectOptionNodeIds: [] },
      lottery: { textNodeId: null, mediaNodeId: null },
      result: {
        lotteryNodeId: null,
        winNodeId: null,
        winTextNodeId: null,
        winMediaNodeId: null,
        loseNodeId: null,
        loseTextNodeId: null,
        loseMediaNodeId: null
      }
    },
    errors: {},
    savedData: null,

    campaignRunning: false,
    uploadLoading: false
  }),
  getters: {
    hasUnsavedChanges(state): boolean {
      if (!state.savedData) return false
      const followCheckChanged =
        state.followCheck.enabled !== state.savedData.followCheck.enabled ||
        state.followCheck.confirmMessage !== state.savedData.followCheck.confirmMessage ||
        state.followCheck.notFollowingMessage !== state.savedData.followCheck.notFollowingMessage
      const messagesChanged = JSON.stringify(state.messages) !== JSON.stringify(state.savedData.messages)
      const lotteryChanged =
        state.lottery.text !== state.savedData.lottery.text ||
        state.lottery.mediaId !== state.savedData.lottery.mediaId ||
        state.lottery.url !== state.savedData.lottery.url
      const resultChanged =
        state.result.winMessage !== state.savedData.result.winMessage ||
        state.result.winMediaId !== state.savedData.result.winMediaId ||
        state.result.winUrl !== state.savedData.result.winUrl ||
        state.result.loseMessage !== state.savedData.result.loseMessage ||
        state.result.loseMediaId !== state.savedData.result.loseMediaId ||
        state.result.loseUrl !== state.savedData.result.loseUrl
      return followCheckChanged || messagesChanged || lotteryChanged || resultChanged
    },
    hasCurrentStepErrors(state): boolean {
      const stepErrors = state.errors[state.currentStep]
      return stepErrors !== undefined && Object.keys(stepErrors).length > 0
    },
    canAddMessage(state): boolean {
      if (state.campaignRunning) return false
      return state.messages.length < 10
    },
    messageCount(state): number {
      return state.messages.length
    },
    canDeleteMessage(state): (index: number) => boolean {
      return (index: number) => {
        if (state.campaignRunning) return false
        return index >= 0 && index < state.messages.length
      }
    },
    canChangeToType(state): (index: number, newType: 'text' | 'media' | 'select' | 'card') => boolean {
      return (index: number) => {
        if (index < 0 || index >= state.messages.length) return false
        if (!state.campaignRunning) return true
        return false
      }
    },
    isStep4Valid(state): boolean {
      const win = state.result.winMessage?.trim() || ''
      const lose = state.result.loseMessage?.trim() || ''
      return win.length > 0 && win.length <= 500 && lose.length > 0 && lose.length <= 500
    }
  },
  actions: {
    async open(prizeId: number): Promise<void> {
      this.prizeId = prizeId
      await this.fetchScenario()
      this.saveFetchedDataAsSnapshot()
    },
    async fetchScenario(): Promise<void> {
      if (!this.prizeId) throw new Error('prizeId is required')
      const responses = await Promise.all([
        getInstagramScenarioContainer(this.prizeId, 1),
        getInstagramScenarioContainer(this.prizeId, 2),
        getInstagramScenarioContainer(this.prizeId, 3),
        getInstagramScenarioContainer(this.prizeId, 4)
      ])
      const step1Data = responses[0]
      this.containerIds.step1 = step1Data.containerId
      this.mapFollowCheckData(step1Data)
      const step2Data = responses[1]
      this.containerIds.step2 = step2Data.containerId
      this.mapMessagesData(step2Data)
      const step3Data = responses[2]
      this.containerIds.step3 = step3Data.containerId
      this.mapLotteryData(step3Data)
      const step4Data = responses[3]
      this.containerIds.step4 = step4Data.containerId
      this.mapResultData(step4Data)
    },
    mapFollowCheckData(data: InstagramScenarioContainerResponseData): void {
      const selectNode = data.nodes.find((node: InstagramScenarioContainerNode) => node.type === 'SELECT')
      const optionYes = selectNode
        ? (selectNode.selectOption || []).find((opt: InstagramScenarioContainerNodeOption) => opt.text === 'はい')
        : null
      const optionNo = selectNode
        ? (selectNode.selectOption || []).find((opt: InstagramScenarioContainerNodeOption) => opt.text === 'いいえ')
        : null
      const notFollowingNode = optionNo
        ? data.nodes.find(
            (node: InstagramScenarioContainerNode) =>
              node.type === 'TEXT' && (node.parentIds || []).includes(optionNo.id)
          ) ||
          data.nodes.find((node: InstagramScenarioContainerNode) => node.type === 'TEXT' && node.isFinally === true)
        : data.nodes.find((node: InstagramScenarioContainerNode) => node.type === 'TEXT' && node.isFinally === true)
      const enabled = !!selectNode && !!notFollowingNode
      this.followCheck = {
        enabled,
        confirmMessage: selectNode?.text ? selectNode.text.replace(/\n/g, '\n') : '',
        notFollowingMessage: notFollowingNode?.text ? notFollowingNode.text.replace(/\n/g, '\n') : ''
      }
      this.nodeIdMap.followCheck.selectId = selectNode?.id || null
      this.nodeIdMap.followCheck.optionYesId = optionYes?.id || null
      this.nodeIdMap.followCheck.optionNoId = optionNo?.id || null
      this.nodeIdMap.followCheck.notFollowingId = notFollowingNode?.id || null
    },
    mapMessagesData(data: InstagramScenarioContainerResponseData): void {
      const firstNode = data.nodes.find((node: InstagramScenarioContainerNode) => node.type === 'FIRST_NODE')
      const messages: MessageNode[] = []
      this.nodeIdMap.messages.firstNodeId = firstNode?.id || null
      this.nodeIdMap.messages.nodeIds = []
      this.nodeIdMap.messages.selectOptionNodeIds = []
      let currentParentIds: Array<number | null> = firstNode ? [firstNode.id] : [null]
      let lastSelectOptionIds: number[] = []
      const processedNodeIds = new Set<number>()
      for (let i = 0; i < 10; i++) {
        const node = data.nodes.find(n => {
          const parents = n.parentIds || []
          const hasParent =
            parents.length === 0
              ? currentParentIds.includes(null)
              : parents.some(p => currentParentIds.includes(p === null ? null : p))
          return hasParent && !processedNodeIds.has(n.id) && n.type !== 'FIRST_NODE' && n.type !== 'SELECT_OPTION'
        })
        if (!node) break
        if (node.isFinally) {
          this.addMessageNodeByType(messages, node)
          break
        }
        processedNodeIds.add(node.id)
        this.addMessageNodeByType(messages, node)
        if (node.type === 'SELECT' && node.selectOption && node.selectOption.length > 0) {
          lastSelectOptionIds = node.selectOption.map(opt => opt.id).filter(Boolean) as number[]
          currentParentIds = lastSelectOptionIds
        } else if (lastSelectOptionIds.length > 0) {
          currentParentIds = [node.id]
          lastSelectOptionIds = []
        } else {
          currentParentIds = [node.id]
        }
      }
      this.nodeIdMap.messages.nodeIds = messages.map(m => m.id || null)
      this.nodeIdMap.messages.selectOptionNodeIds = messages.map(m =>
        m.type === 'select' ? m.options.map(o => o.id || null) : []
      )
      this.messages = messages
    },
    addMessageNodeByType(messages: MessageNode[], node: InstagramScenarioContainerNode): void {
      switch (node.type) {
        case 'TEXT':
          messages.push({ type: 'text', text: node.text ? node.text.replace(/\n/g, '\n') : '', id: node.id })
          break
        case 'MEDIA':
          messages.push({
            type: 'media',
            mediaId: node.media?.media_id || null,
            fileName: node.media?.mediaOriginName || undefined,
            url: node.media?.url || null,
            fileType: node.media?.fileType?.startsWith('image/')
              ? 'image'
              : node.media?.fileType?.startsWith('video/')
                ? 'video'
                : undefined,
            id: node.id
          })
          break
        case 'SELECT':
          messages.push({
            type: 'select',
            question: node.text ? node.text.replace(/\n/g, '\n') : '',
            options: (node.selectOption || []).map((opt: InstagramScenarioContainerNodeOption) => ({
              text: opt.text || '',
              id: opt.id || null
            })),
            id: node.id
          })
          break
        case 'CARD':
          messages.push({
            type: 'card',
            mediaId: node.card?.mediaId || null,
            fileName: node.card?.mediaOriginName || undefined,
            url: node.card?.url || null,
            title: node.card?.title ? node.card.title.replace(/\n/g, '\n') : '',
            subtitle: node.card?.subtitle ? node.card.subtitle.replace(/\n/g, '\n') : '',
            defaultUrl: node.card?.cardUrl || '',
            buttons: (node.card?.buttons || []).map(btn => ({
              label: btn.name || '',
              url: btn.url || '',
              id: btn.id || null
            })),
            id: node.id
          })
          break
      }
    },
    mapLotteryData(data: InstagramScenarioContainerResponseData): void {
      let text = ''
      let mediaId: number | null = null
      let url: string | null = null
      let fileType: 'image' | 'video' | undefined = undefined
      // eslint-disable-next-line prefer-const
      let fileName: string | undefined = undefined
      const textNode = data.nodes.find((node: InstagramScenarioContainerNode) => node.type === 'TEXT')
      if (textNode?.text) text = textNode.text.replace(/\n/g, '\n')
      const mediaNode = data.nodes.find((node: InstagramScenarioContainerNode) => node.type === 'MEDIA')
      if (mediaNode?.media) {
        mediaId = mediaNode.media.media_id || null
        url = mediaNode.media.url || null
        fileName = mediaNode.media.mediaOriginName || undefined
        const rawFileType = mediaNode.media.fileType
        if (rawFileType) {
          if (rawFileType.startsWith('image/')) fileType = 'image'
          else if (rawFileType.startsWith('video/')) fileType = 'video'
        }
      }
      this.lottery = { text, mediaId, url, fileType, fileName }
      this.nodeIdMap.lottery.textNodeId = textNode?.id || null
      this.nodeIdMap.lottery.mediaNodeId = mediaNode?.id || null
    },
    mapResultData(data: InstagramScenarioContainerResponseData): void {
      const winNode = data.nodes.find((node: InstagramScenarioContainerNode) => node.type === 'LOTTERY_WIN')
      const loseNode = data.nodes.find((node: InstagramScenarioContainerNode) => node.type === 'LOTTERY_LOSE')
      const getParents = (node: InstagramScenarioContainerNode) => node.parentIds || []
      if (!winNode || !loseNode) {
        this.result = {
          winMessage: '',
          winMediaId: null,
          winUrl: null,
          loseMessage: '',
          loseMediaId: null,
          loseUrl: null
        }
        return
      }
      const winTextNode = data.nodes.find(
        (node: InstagramScenarioContainerNode) =>
          node.type === 'TEXT' && getParents(node).includes(winNode.id as number)
      )
      let winMediaNode = data.nodes.find(
        (node: InstagramScenarioContainerNode) =>
          node.type === 'MEDIA' && getParents(node).includes(winNode.id as number)
      )
      if (!winMediaNode && winTextNode)
        winMediaNode = data.nodes.find(
          (node: InstagramScenarioContainerNode) =>
            node.type === 'MEDIA' && getParents(node).includes(winTextNode.id as number)
        )
      const loseTextNode = data.nodes.find(
        (node: InstagramScenarioContainerNode) =>
          node.type === 'TEXT' && getParents(node).includes(loseNode.id as number)
      )
      let loseMediaNode = data.nodes.find(
        (node: InstagramScenarioContainerNode) =>
          node.type === 'MEDIA' && getParents(node).includes(loseNode.id as number)
      )
      if (!loseMediaNode && loseTextNode)
        loseMediaNode = data.nodes.find(
          (node: InstagramScenarioContainerNode) =>
            node.type === 'MEDIA' && getParents(node).includes(loseTextNode.id as number)
        )
      let winFileType: 'image' | 'video' | undefined = undefined
      if (winMediaNode?.media?.fileType) {
        const raw = winMediaNode.media.fileType
        if (raw.startsWith('image/')) winFileType = 'image'
        else if (raw.startsWith('video/')) winFileType = 'video'
      }
      let loseFileType: 'image' | 'video' | undefined = undefined
      if (loseMediaNode?.media?.fileType) {
        const raw2 = loseMediaNode.media.fileType
        if (raw2.startsWith('image/')) loseFileType = 'image'
        else if (raw2.startsWith('video/')) loseFileType = 'video'
      }
      this.result = {
        winMessage: winTextNode?.text ? winTextNode.text.replace(/\n/g, '\n') : '',
        winMediaId: winMediaNode?.media?.media_id || null,
        winUrl: winMediaNode?.media?.url || null,
        winFileType,
        winFileName: winMediaNode?.media?.mediaOriginName || undefined,
        loseMessage: loseTextNode?.text ? loseTextNode.text.replace(/\n/g, '\n') : '',
        loseMediaId: loseMediaNode?.media?.media_id || null,
        loseUrl: loseMediaNode?.media?.url || null,
        loseFileType,
        loseFileName: loseMediaNode?.media?.mediaOriginName || undefined
      }
      const lotteryNode = data.nodes.find((node: InstagramScenarioContainerNode) => node.type === 'LOTTERY_NODE')
      this.nodeIdMap.result.lotteryNodeId = lotteryNode?.id || null
      this.nodeIdMap.result.winNodeId = winNode?.id || null
      this.nodeIdMap.result.winTextNodeId = winTextNode?.id || null
      this.nodeIdMap.result.winMediaNodeId = winMediaNode?.id || null
      this.nodeIdMap.result.loseNodeId = loseNode?.id || null
      this.nodeIdMap.result.loseTextNodeId = loseTextNode?.id || null
      this.nodeIdMap.result.loseMediaNodeId = loseMediaNode?.id || null
    },
    saveFetchedDataAsSnapshot(): void {
      this.savedData = {
        followCheck: { ...this.followCheck },
        messages: JSON.parse(JSON.stringify(this.messages)),
        lottery: { ...this.lottery },
        result: { ...this.result }
      }
    },
    async save(): Promise<void> {
      if (!this.prizeId) throw new Error('prizeId is required')
      const requestBody = this.convertToApiRequestBody()
      const result = await putInstagramScenarioContainers(this.prizeId, requestBody.containers)
      if ('error' in result) {
        throw new Error(result.error.message || 'シナリオの保存に失敗しました。')
      }
      this.saveFetchedDataAsSnapshot()
      this.reset()
    },
    convertToApiRequestBody(): { containers: RequestContainer[] } {
      const containers: RequestContainer[] = []
      containers.push({ id: this.containerIds.step1, stepOrder: 1, nodes: this.convertFollowCheckToNodes() })
      containers.push({ id: this.containerIds.step2, stepOrder: 2, nodes: this.convertMessagesToNodes() })
      containers.push({ id: this.containerIds.step3, stepOrder: 3, nodes: this.convertLotteryToNodes() })
      containers.push({ id: this.containerIds.step4, stepOrder: 4, nodes: this.convertResultToNodes() })
      return { containers }
    },
    convertFollowCheckToNodes(): RequestNode[] {
      const nodes: RequestNode[] = []
      const sId = this.nodeIdMap.followCheck.selectId
      const yesId = this.nodeIdMap.followCheck.optionYesId
      const noId = this.nodeIdMap.followCheck.optionNoId
      const nfId = this.nodeIdMap.followCheck.notFollowingId
      if (!this.followCheck.enabled) {
        return nodes
      }
      const selectNode: RequestNode = { type: 4, text: this.followCheck.confirmMessage, isFinally: false }
      if (sId) {
        selectNode.id = sId
      } else {
        selectNode.tempId = 'follow-check-select'
      }
      nodes.push(selectNode)
      const yesNode: RequestNode = { type: 5, text: 'はい', isFinally: false }
      if (yesId) {
        yesNode.id = yesId
        if (sId) yesNode.parentIds = [sId]
        else yesNode.parentTempIds = ['follow-check-select']
      } else {
        yesNode.tempId = 'follow-check-option-yes'
        if (sId) yesNode.parentIds = [sId]
        else yesNode.parentTempIds = ['follow-check-select']
      }
      nodes.push(yesNode)
      const noNode: RequestNode = { type: 5, text: 'いいえ', isFinally: false }
      if (noId) {
        noNode.id = noId
        if (sId) noNode.parentIds = [sId]
        else noNode.parentTempIds = ['follow-check-select']
      } else {
        noNode.tempId = 'follow-check-option-no'
        if (sId) noNode.parentIds = [sId]
        else noNode.parentTempIds = ['follow-check-select']
      }
      nodes.push(noNode)
      const nfNode: RequestNode = { type: 1, text: this.followCheck.notFollowingMessage, isFinally: true }
      if (nfId) {
        nfNode.id = nfId
        if (noId) nfNode.parentIds = [noId]
        else nfNode.parentTempIds = ['follow-check-option-no']
      } else {
        nfNode.tempId = 'follow-check-not-following'
        if (noId) nfNode.parentIds = [noId]
        else nfNode.parentTempIds = ['follow-check-option-no']
      }
      nodes.push(nfNode)
      return nodes
    },
    convertMessagesToNodes(): RequestNode[] {
      const nodes: RequestNode[] = []
      if (this.messages.length === 0) return nodes
      let prevExistingParents: number[] = []
      let prevTempParents: string[] = []
      this.messages.forEach((message, index) => {
        const makeNodeBase = (typeNum: number): RequestNode => ({ type: typeNum, isFinally: false })
        const attachParent = (node: RequestNode) => {
          if (prevExistingParents.length > 0) node.parentIds = [...prevExistingParents]
          else if (prevTempParents.length > 0) node.parentTempIds = [...prevTempParents]
        }
        switch (message.type) {
          case 'text': {
            const node: RequestNode = makeNodeBase(1)
            node.text = message.text
            if (message.id) node.id = message.id
            else node.tempId = `message-${index}`
            attachParent(node)
            nodes.push(node)
            prevExistingParents = message.id ? [message.id] : []
            prevTempParents = !message.id ? [`message-${index}`] : []
            break
          }
          case 'media': {
            if (message.mediaId) {
              const node: RequestNode = makeNodeBase(2)
              node.mediaId = message.mediaId
              if (message.id) node.id = message.id
              else node.tempId = `message-${index}`
              attachParent(node)
              nodes.push(node)
              prevExistingParents = message.id ? [message.id] : []
              prevTempParents = !message.id ? [`message-${index}`] : []
            }
            break
          }
          case 'select': {
            const selectNode: RequestNode = { type: 4, isFinally: false, text: message.question }
            if (message.id) selectNode.id = message.id
            else selectNode.tempId = `message-${index}`
            attachParent(selectNode)
            nodes.push(selectNode)
            const optionExistingIds: number[] = []
            const optionTempIds: string[] = []
            message.options.forEach((option, optIdx) => {
              const optNode: RequestNode = { type: 5, text: option.text, isFinally: false }
              if (option.id) optNode.id = option.id
              else optNode.tempId = `message-${index}-option-${optIdx}`
              if (message.id) optNode.parentIds = [message.id]
              else optNode.parentTempIds = [`message-${index}`]
              nodes.push(optNode)
              if (option.id) optionExistingIds.push(option.id)
              else optionTempIds.push(`message-${index}-option-${optIdx}`)
            })
            prevExistingParents = optionExistingIds
            prevTempParents = optionTempIds
            break
          }
          case 'card': {
            if (message.mediaId) {
              const cardNode: RequestNode = {
                type: 3,
                isFinally: false,
                card: {
                  title: message.title,
                  subtitle: message.subtitle,
                  url: message.defaultUrl.trim() ? message.defaultUrl.trim() : null,
                  mediaId: message.mediaId,
                  button: [] as RequestCardButton[]
                }
              }
              if (message.id) cardNode.id = message.id
              else cardNode.tempId = `message-${index}`
              attachParent(cardNode)
              message.buttons.forEach((btn, btnIdx) => {
                const btnObj: RequestCardButton = { name: btn.label, url: btn.url }
                if (btn.id) btnObj.id = btn.id
                else btnObj.tempId = `message-${index}-button-${btnIdx}`
                cardNode.card?.button.push(btnObj) // 非null前提だがlint警告回避
              })
              nodes.push(cardNode)
              prevExistingParents = message.id ? [message.id] : []
              prevTempParents = !message.id ? [`message-${index}`] : []
            }
            break
          }
        }
      })
      return nodes
    },
    convertLotteryToNodes(): RequestNode[] {
      const nodes: RequestNode[] = []
      const textId = this.nodeIdMap.lottery.textNodeId
      const mediaIdExisting = this.nodeIdMap.lottery.mediaNodeId
      if (this.lottery.text) {
        const textNode: RequestNode = {
          type: 1,
          text: this.lottery.text,
          isFinally: false,
          parentIds: []
        }
        if (textId) textNode.id = textId
        else textNode.tempId = 'lottery-text'
        nodes.push(textNode)
        if (this.lottery.mediaId) {
          const mediaNode: RequestNode = { type: 2, mediaId: this.lottery.mediaId ?? undefined, isFinally: false }
          if (mediaIdExisting) {
            mediaNode.id = mediaIdExisting
            if (textId) mediaNode.parentIds = [textId]
            else mediaNode.parentTempIds = ['lottery-text']
          } else {
            mediaNode.tempId = 'lottery-media'
            if (textId) mediaNode.parentIds = [textId]
            else mediaNode.parentTempIds = ['lottery-text']
          }
          nodes.push(mediaNode)
        }
        return nodes
      }
      if (this.lottery.mediaId) {
        const mediaNode: RequestNode = {
          type: 2,
          mediaId: this.lottery.mediaId ?? undefined,
          isFinally: false,
          parentIds: []
        }
        if (mediaIdExisting) mediaNode.id = mediaIdExisting
        else mediaNode.tempId = 'lottery-media'
        nodes.push(mediaNode)
      }
      return nodes
    },
    convertResultToNodes(): RequestNode[] {
      const nodes: RequestNode[] = []
      const lotNodeId = this.nodeIdMap.result.lotteryNodeId
      const winNodeId = this.nodeIdMap.result.winNodeId
      const winTextId = this.nodeIdMap.result.winTextNodeId
      const winMediaIdExisting = this.nodeIdMap.result.winMediaNodeId
      const loseNodeId = this.nodeIdMap.result.loseNodeId
      const loseTextId = this.nodeIdMap.result.loseTextNodeId
      const loseMediaIdExisting = this.nodeIdMap.result.loseMediaNodeId
      const lotteryNode: RequestNode = { type: 6, isFinally: false, parentIds: [] }
      if (lotNodeId) lotteryNode.id = lotNodeId
      else lotteryNode.tempId = 'lottery-node'
      nodes.push(lotteryNode)
      const winNode: RequestNode = { type: 7, isFinally: false }
      if (winNodeId) {
        winNode.id = winNodeId
        if (lotNodeId) winNode.parentIds = [lotNodeId]
        else winNode.parentTempIds = ['lottery-node']
      } else {
        winNode.tempId = 'result-win'
        if (lotNodeId) winNode.parentIds = [lotNodeId]
        else winNode.parentTempIds = ['lottery-node']
      }
      nodes.push(winNode)
      if (this.result.winMessage) {
        const winTextNode: RequestNode = { type: 1, text: this.result.winMessage, isFinally: !this.result.winMediaId }
        if (winTextId) {
          winTextNode.id = winTextId
          if (winNodeId) winTextNode.parentIds = [winNodeId]
          else winTextNode.parentTempIds = ['result-win']
        } else {
          winTextNode.tempId = 'result-win-text'
          if (winNodeId) winTextNode.parentIds = [winNodeId]
          else winTextNode.parentTempIds = ['result-win']
        }
        // WIN ブランチ末端は media がある場合 media が finally、なければ text が finally
        if (!this.result.winMediaId) winTextNode.isFinally = true
        else winTextNode.isFinally = false
        nodes.push(winTextNode)
        if (this.result.winMediaId) {
          const winMediaNode: RequestNode = { type: 2, mediaId: this.result.winMediaId ?? undefined, isFinally: true }
          const winTextRefExisting = winTextId || null
          if (winMediaIdExisting) {
            winMediaNode.id = winMediaIdExisting
            if (winTextRefExisting) winMediaNode.parentIds = [winTextRefExisting]
            else winMediaNode.parentTempIds = ['result-win-text']
          } else {
            winMediaNode.tempId = 'result-win-media'
            if (winTextRefExisting) winMediaNode.parentIds = [winTextRefExisting]
            else winMediaNode.parentTempIds = ['result-win-text']
          }
          nodes.push(winMediaNode)
        }
      }
      const loseNode: RequestNode = { type: 8, isFinally: false }
      if (loseNodeId) {
        loseNode.id = loseNodeId
        if (lotNodeId) loseNode.parentIds = [lotNodeId]
        else loseNode.parentTempIds = ['lottery-node']
      } else {
        loseNode.tempId = 'result-lose'
        if (lotNodeId) loseNode.parentIds = [lotNodeId]
        else loseNode.parentTempIds = ['lottery-node']
      }
      nodes.push(loseNode)
      if (this.result.loseMessage) {
        const loseTextNode: RequestNode = {
          type: 1,
          text: this.result.loseMessage,
          isFinally: !this.result.loseMediaId
        }
        if (loseTextId) {
          loseTextNode.id = loseTextId
          if (loseNodeId) loseTextNode.parentIds = [loseNodeId]
          else loseTextNode.parentTempIds = ['result-lose']
        } else {
          loseTextNode.tempId = 'result-lose-text'
          if (loseNodeId) loseTextNode.parentIds = [loseNodeId]
          else loseTextNode.parentTempIds = ['result-lose']
        }
        if (!this.result.loseMediaId) loseTextNode.isFinally = true
        else loseTextNode.isFinally = false
        nodes.push(loseTextNode)
        if (this.result.loseMediaId) {
          const loseMediaNode: RequestNode = { type: 2, mediaId: this.result.loseMediaId ?? undefined, isFinally: true }
          const loseTextRefExisting = loseTextId || null
          if (loseMediaIdExisting) {
            loseMediaNode.id = loseMediaIdExisting
            if (loseTextRefExisting) loseMediaNode.parentIds = [loseTextRefExisting]
            else loseMediaNode.parentTempIds = ['result-lose-text']
          } else {
            loseMediaNode.tempId = 'result-lose-media'
            if (loseTextRefExisting) loseMediaNode.parentIds = [loseTextRefExisting]
            else loseMediaNode.parentTempIds = ['result-lose-text']
          }
          nodes.push(loseMediaNode)
        }
      }
      return nodes
    },
    goToNextStep(): void {
      if (this.currentStep < 4) this.currentStep = (this.currentStep + 1) as 1 | 2 | 3 | 4
    },
    goToPreviousStep(): void {
      if (this.currentStep > 1) this.currentStep = (this.currentStep - 1) as 1 | 2 | 3 | 4
    },
    validateStep1(): boolean {
      // 既存ステップ1のエラーを除去
      const { 1: _removed, ...rest } = this.errors
      void _removed
      this.errors = rest
      if (!this.followCheck.enabled) return true
      const errors: { [field: string]: string } = {}
      const confirmMessage = this.followCheck.confirmMessage.trim()
      if (confirmMessage === '') errors.confirmMessage = 'フォロー確認メッセージを入力してください。'
      else if (confirmMessage.length > 500) errors.confirmMessage = '文字数は500文字以内にしてください。'
      const notFollowingMessage = this.followCheck.notFollowingMessage.trim()
      if (notFollowingMessage === '') errors.notFollowingMessage = 'フォローしてない場合のコメントを入力してください。'
      else if (notFollowingMessage.length > 500) errors.notFollowingMessage = '文字数は500文字以内にしてください。'
      if (Object.keys(errors).length > 0) {
        this.errors = { ...this.errors, [1]: errors }
        return false
      }
      return true
    },
    validateStep2(): boolean {
      // 既存ステップ2のエラーを除去
      const { 2: _removed, ...rest } = this.errors
      void _removed
      this.errors = rest
      const errors: { [field: string]: string } = {}
      this.messages.forEach((message, index) => {
        if (message.type === 'text') {
          const text = message.text.trim()
          if (text === '') errors[`messages.${index}.text`] = 'テキストを入力してください。'
          else if (text.length > 500) errors[`messages.${index}.text`] = '文字数は500文字以内にしてください。'
        } else if (message.type === 'media') {
          if (!message.mediaId) errors[`messages.${index}.media`] = '画像または動画を追加してください。'
        } else if (message.type === 'select') {
          const question = message.question.trim()
          if (question === '') errors[`messages.${index}.question`] = '質問を入力してください。'
          else if (question.length > 500) errors[`messages.${index}.question`] = '文字数は500文字以内にしてください。'
          if (message.options.length < 2) errors[`messages.${index}.options`] = '選択肢は2個以上追加してください。'
          else if (message.options.length > 13) errors[`messages.${index}.options`] = '選択肢は13個までです。'
          message.options.forEach((option, optionIndex) => {
            const optionText = option.text.trim()
            if (optionText === '') errors[`messages.${index}.options.${optionIndex}`] = '選択肢を入力してください。'
            else if (optionText.length > 20)
              errors[`messages.${index}.options.${optionIndex}`] = '20文字以内で入力してください。'
          })
        } else if (message.type === 'card') {
          if (!message.mediaId) errors[`messages.${index}.media`] = '画像を追加してください。'
          const title = message.title.trim()
          if (title === '') errors[`messages.${index}.title`] = 'タイトルを入力してください。'
          const subtitle = message.subtitle.trim()

          if (title.length > 80) errors[`messages.${index}.title`] = '80文字以内で入力してください。'
          if (subtitle.length > 80) errors[`messages.${index}.subtitle`] = '80文字以内で入力してください。'
          const defaultUrl = (message.defaultUrl || '').trim()
          if (defaultUrl !== '' && !/^https?:\/\//.test(defaultUrl))
            errors[`messages.${index}.defaultUrl`] = 'URLはhttp(s)で始まる必要があります。'
          message.buttons.forEach((button, buttonIndex) => {
            const label = (button.label || '').trim()
            if (label === '')
              errors[`messages.${index}.buttons.${buttonIndex}.label`] = 'ボタンのラベルを入力してください。'
            else if (label.length > 20)
              errors[`messages.${index}.buttons.${buttonIndex}.label`] = '文字数は20文字以内にしてください。'
            const url = (button.url || '').trim()
            if (url === '') errors[`messages.${index}.buttons.${buttonIndex}.url`] = 'ボタンのURLを入力してください。'
            else if (!/^https?:\/\//.test(url))
              errors[`messages.${index}.buttons.${buttonIndex}.url`] = 'URLはhttp(s)で始まる必要があります。'
          })
        }
      })
      if (Object.keys(errors).length > 0) {
        this.errors = { ...this.errors, [2]: errors }
        return false
      }
      return true
    },
    validateCurrentStep(): boolean {
      switch (this.currentStep) {
        case 1:
          return this.validateStep1()
        case 2:
          return this.validateStep2()
        case 3:
          return this.validateStep3()
        case 4:
          return this.validateStep4()
        default:
          return true
      }
    },
    close(): void {
      this.reset()
    },
    reset(): void {
      this.prizeId = null
      this.containerIds = { step1: null, step2: null, step3: null, step4: null }
      this.currentStep = 1
      this.followCheck = {
        enabled: true,
        confirmMessage: 'あなたはアカウントをフォローしてますか?',
        notFollowingMessage: 'フォロワー限定のキャンペーンです！プロフィールからアカウントをフォローしてください！'
      }
      this.messages = []
      this.lottery = {
        text: 'ただいま抽選中です！',
        mediaId: null,
        url: null,
        fileType: undefined,
        fileName: undefined
      }
      this.result = {
        winMessage: '',
        winMediaId: null,
        winUrl: null,
        winFileType: undefined,
        winFileName: undefined,
        loseMessage: '',
        loseMediaId: null,
        loseUrl: null,
        loseFileType: undefined,
        loseFileName: undefined
      }
      this.nodeIdMap = {
        followCheck: { selectId: null, optionYesId: null, optionNoId: null, notFollowingId: null },
        messages: { firstNodeId: null, nodeIds: [], selectOptionNodeIds: [] },
        lottery: { textNodeId: null, mediaNodeId: null },
        result: {
          lotteryNodeId: null,
          winNodeId: null,
          winTextNodeId: null,
          winMediaNodeId: null,
          loseNodeId: null,
          loseTextNodeId: null,
          loseMediaNodeId: null
        }
      }
      this.errors = {}
      this.savedData = null

      this.campaignRunning = false
    },
    addMessage(): void {
      if (this.campaignRunning) return
      if (this.messages.length >= 10) return
      this.messages.push({ type: 'text', text: '' })
    },
    deleteMessage(index: number): void {
      if (this.campaignRunning) return
      if (index < 0 || index >= this.messages.length) return
      this.messages.splice(index, 1)
    },
    updateMessageType(index: number, newType: 'text' | 'media' | 'select' | 'card'): void {
      if (index < 0 || index >= this.messages.length) return
      if (this.campaignRunning && (newType === 'select' || newType === 'media')) return
      let newNode: MessageNode
      switch (newType) {
        case 'text':
          newNode = { type: 'text', text: '' }
          break
        case 'media':
          newNode = { type: 'media', mediaId: null, url: null, fileName: undefined }
          break
        case 'select':
          newNode = { type: 'select', question: '', options: [] }
          break
        case 'card':
          newNode = {
            type: 'card',
            mediaId: null,
            url: null,
            title: '',
            subtitle: '',
            defaultUrl: '',
            buttons: [],
            fileName: undefined
          }
          break
      }
      this.messages.splice(index, 1, newNode)
    },
    setCampaignRunning(running: boolean): void {
      this.campaignRunning = running
    },
    updateMediaNode(
      index: number,
      updates: { url?: string | null; mediaId?: number | null; fileType?: 'image' | 'video'; fileName?: string }
    ): void {
      if (index < 0 || index >= this.messages.length) return
      const message = this.messages[index]
      if (message.type !== 'media') return
      const updatedMessage = Object.assign({}, message, updates) as MediaNode
      const newMessages = [...this.messages]
      newMessages[index] = updatedMessage
      this.messages = newMessages
    },
    clearMediaNode(index: number): void {
      if (index < 0 || index >= this.messages.length) return
      const message = this.messages[index]
      if (message.type !== 'media') return
      const newMessages = [...this.messages]
      newMessages[index] = {
        ...message,
        type: 'media',
        url: null,
        mediaId: null,
        fileType: undefined,
        fileName: undefined
      }
      this.messages = newMessages
    },
    updateQuickReplyNode(index: number, question?: string, options?: Array<{ text: string }>): void {
      if (index < 0 || index >= this.messages.length) return
      const message = this.messages[index]
      if (message.type !== 'select') return
      const updatedMessage: SelectNode = {
        type: 'select',
        question: question !== undefined ? question : message.question,
        options: options !== undefined ? options : message.options,
        id: message.id
      }
      const newMessages = [...this.messages]
      newMessages[index] = updatedMessage
      this.messages = newMessages
    },
    addQuickReplyOption(index: number): void {
      if (this.campaignRunning) return
      if (index < 0 || index >= this.messages.length) return
      const message = this.messages[index]
      if (message.type !== 'select') return
      if (message.options.length >= 13) return
      const newOptions = [...message.options, { text: '' }]
      this.updateQuickReplyNode(index, undefined, newOptions)
    },
    removeQuickReplyOption(index: number, optionIndex: number): void {
      if (this.campaignRunning) return
      if (index < 0 || index >= this.messages.length) return
      const message = this.messages[index]
      if (message.type !== 'select') return
      if (optionIndex < 0 || optionIndex >= message.options.length) return
      const newOptions = message.options.filter((_, i) => i !== optionIndex)
      this.updateQuickReplyNode(index, undefined, newOptions)
    },
    updateCardNode(
      index: number,
      updates: {
        url?: string | null
        mediaId?: number | null
        title?: string
        subtitle?: string
        defaultUrl?: string
        buttons?: Array<{ label: string; url: string }>
        fileName?: string
      }
    ): void {
      if (index < 0 || index >= this.messages.length) return
      const message = this.messages[index]
      if (message.type !== 'card') return
      const updatedMessage: CardNode = {
        ...message,
        type: 'card',
        url: updates.url !== undefined ? updates.url : message.url,
        mediaId: updates.mediaId !== undefined ? updates.mediaId : message.mediaId,
        title: updates.title !== undefined ? updates.title : message.title,
        subtitle: updates.subtitle !== undefined ? updates.subtitle : message.subtitle,
        defaultUrl: updates.defaultUrl !== undefined ? updates.defaultUrl : message.defaultUrl,
        buttons: updates.buttons !== undefined ? updates.buttons : message.buttons,
        fileName: updates.fileName !== undefined ? updates.fileName : message.fileName
      }
      const newMessages = [...this.messages]
      newMessages[index] = updatedMessage
      this.messages = newMessages
    },
    addCardButton(index: number): void {
      if (this.campaignRunning) return
      if (index < 0 || index >= this.messages.length) return
      const message = this.messages[index]
      if (message.type !== 'card') return
      if (message.buttons.length >= 3) return
      const newButtons = [...message.buttons, { label: '', url: '' }]
      this.updateCardNode(index, { buttons: newButtons })
    },
    removeCardButton(index: number, buttonIndex: number): void {
      if (this.campaignRunning) return
      if (index < 0 || index >= this.messages.length) return
      const message = this.messages[index]
      if (message.type !== 'card') return
      if (buttonIndex < 0 || buttonIndex >= message.buttons.length) return
      const newButtons = message.buttons.filter((_, i) => i !== buttonIndex)
      this.updateCardNode(index, { buttons: newButtons })
    },
    clearCardMedia(index: number): void {
      this.updateCardNode(index, { url: null, mediaId: null })
    },
    async uploadMedia(
      file: File
    ): Promise<{ mediaId: number; url: string; attachmentId: string; mediaOriginName: string | null }> {
      if (!this.prizeId) throw new Error('prizeId is required')
      this.uploadLoading = true
      try {
        const formData = new FormData()
        formData.append('prize_id', String(this.prizeId))
        formData.append('file', file)
        const response = await fetch('/api/instagram_instantwin_node_container/upload_media', {
          method: 'POST',
          body: formData
        })
        if (!response.ok) {
          const errorData = await response.json()
          const message = errorData.error?.message || 'Unknown error'
          throw new Error(`Failed to upload media: ${message}`)
        }
        const data = await response.json()
        return {
          mediaId: data.data.media_id,
          url: data.data.url,
          attachmentId: data.data.attachment_id,
          mediaOriginName: data.data.mediaOriginName || null
        }
      } finally {
        this.uploadLoading = false
      }
    },
    validateStep3(): boolean {
      // 既存ステップ3のエラーを除去
      const { 3: _removed, ...rest } = this.errors
      void _removed
      this.errors = rest
      const errors: { [field: string]: string } = {}
      if (!this.lottery.text || this.lottery.text.trim() === '') errors.text = '抽選中テキストを入力してください'
      else if (this.lottery.text.length > 500) errors.text = '文字数は500文字以内にしてください'
      if (Object.keys(errors).length > 0) {
        this.errors = { ...this.errors, 3: errors }
        return false
      }
      return true
    },
    validateStep4(): boolean {
      // 既存ステップ4のエラーを除去
      const { 4: _removed, ...rest } = this.errors
      void _removed
      this.errors = rest
      const errors: { [field: string]: string } = {}
      if (!this.result.winMessage || this.result.winMessage.trim() === '')
        errors.winMessage = '当選メッセージを入力してください。'
      else if (this.result.winMessage.length > 500) errors.winMessage = '文字数は500文字以内にしてください。'
      if (!this.result.loseMessage || this.result.loseMessage.trim() === '')
        errors.loseMessage = '落選メッセージを入力してください。'
      else if (this.result.loseMessage.length > 500) errors.loseMessage = '文字数は500文字以内にしてください。'
      if (Object.keys(errors).length > 0) {
        this.errors = { ...this.errors, 4: errors }
        return false
      }
      return true
    },
    updateLottery(updates: {
      text?: string
      url?: string | null
      mediaId?: number | null
      fileType?: 'image' | 'video'
      fileName?: string
    }): void {
      if (updates.text !== undefined) this.lottery.text = updates.text
      if (updates.url !== undefined) this.lottery.url = updates.url
      if (updates.mediaId !== undefined) this.lottery.mediaId = updates.mediaId
      if (updates.fileType !== undefined) this.lottery.fileType = updates.fileType
      if (updates.fileName !== undefined) this.lottery.fileName = updates.fileName
    },
    clearLotteryMedia(): void {
      this.lottery.url = null
      this.lottery.mediaId = null
      this.lottery.fileType = undefined
      this.lottery.fileName = undefined
    },
    updateResult(updates: {
      winMessage?: string
      winUrl?: string | null
      winMediaId?: number | null
      winFileType?: 'image' | 'video'
      winFileName?: string
      loseMessage?: string
      loseUrl?: string | null
      loseMediaId?: number | null
      loseFileType?: 'image' | 'video'
      loseFileName?: string
    }): void {
      if (updates.winMessage !== undefined) this.result.winMessage = updates.winMessage
      if (updates.winUrl !== undefined) this.result.winUrl = updates.winUrl
      if (updates.winMediaId !== undefined) this.result.winMediaId = updates.winMediaId
      if (updates.winFileType !== undefined) this.result.winFileType = updates.winFileType
      if (updates.winFileName !== undefined) this.result.winFileName = updates.winFileName
      if (updates.loseMessage !== undefined) this.result.loseMessage = updates.loseMessage
      if (updates.loseUrl !== undefined) this.result.loseUrl = updates.loseUrl
      if (updates.loseMediaId !== undefined) this.result.loseMediaId = updates.loseMediaId
      if (updates.loseFileType !== undefined) this.result.loseFileType = updates.loseFileType
      if (updates.loseFileName !== undefined) this.result.loseFileName = updates.loseFileName
    },
    clearWinMedia(): void {
      this.result.winUrl = null
      this.result.winMediaId = null
      this.result.winFileType = undefined
      this.result.winFileName = undefined
    },
    clearLoseMedia(): void {
      this.result.loseUrl = null
      this.result.loseMediaId = null
      this.result.loseFileType = undefined
      this.result.loseFileName = undefined
    }
  }
})
